import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { performDocumentsMassAction } from '../../../../../../api'
import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../../../../constants/file'
import { HTML_LINK_TARGET } from '../../../../../../constants/html'
import { SUBMENU_ALL_SECTION } from '../../../../../../constants/route'
import {
  useLinkLikeClick,
  useMinimizedModalsContext,
  useNotification,
  useOutboundUuid,
} from '../../../../../../hooks'
import { getAsyncTaskId } from '../../../../../../utils/async'
import { ConfirmationModal } from '../../../../../Confirmation'
import { DOCUMENTS_ACTION_DOWNLOAD } from '../../../../../Document'
import { useFilesDownloadAll } from '../../../../../File'
import {
  AttachServiceRequestsModal,
  getRequestThreadPrintPageLink,
} from '../../../../../Request'
import {
  deleteRequestThread,
  moveRequestThreadToRequest,
  toggleRequestThreadRead,
} from '../../../RequestProfileTabEmails.api'
import { REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION } from '../../../RequestProfileTabEmails.constants'
import {
  getRequestProfileTabEmailsItemActionsErrorTitleKey,
  getRequestProfileTabEmailsItemActionsNotificationTitleKey,
  useRequestProfileTabEmailsItemActions,
} from '../../../RequestProfileTabEmails.hooks'

import {
  getHasFiles,
  getIconBarActions,
  getMenuActions,
} from './useRequestProfileTabEmailsThreadModalHeaderActions.utils'

export function useRequestProfileTabEmailsThreadModalHeaderActions({
  directoryId,
  entityData,
  mailUuid,
  messages,
  minimizedModalScope,
  requestId,
  setModal,
  onClose,
  onReloadList,
  onReloadProfile,
  onThreadRemoved,
}) {
  const { t } = useTranslation()

  const { confirmBeforeAction } = useMinimizedModalsContext()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const outboundUuid = useOutboundUuid()

  const [isPerformingAction, setPerformingAction] = useState(false)

  const { permissions = EMPTY_OBJECT, starred } = entityData ?? EMPTY_OBJECT

  function handleCloseNestedModal() {
    setModal(null)
  }

  function handleReload() {
    return Promise.allSettled([onReloadProfile?.(), onReloadList?.()])
  }

  const { setImportant, setItemAction } = useRequestProfileTabEmailsItemActions(
    {
      requestId,
      setPerformingAction,
      showDeleteConfirmModal: handleCloseNestedModal,
      onCloseModal: handleCloseNestedModal,
      onReload: handleReload,
      onSetModal: setModal,
    }
  )

  async function handleSubmitDeleteThread() {
    const action = REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.Delete

    setModal(null)
    setPerformingAction(true)

    return deleteRequestThread({ requestId, uuid: mailUuid })
      .then(() => {
        handleAddSuccessNotification(
          getRequestProfileTabEmailsItemActionsNotificationTitleKey(action)
        )
        onReloadList?.()

        return onThreadRemoved?.()
      })
      .catch(() => {
        handleAddErrorNotification(
          getRequestProfileTabEmailsItemActionsErrorTitleKey(action)
        )
      })
      .finally(() => {
        setPerformingAction(false)
      })
  }

  function handleDeleteThread() {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{ submitText: t('Modal.DeleteBtnKey') }}
        description={t('RequestProfile.TabEmailsDeleteConfirmationKey')}
        onCancel={handleCloseNestedModal}
        onSubmit={handleSubmitDeleteThread}
      />
    )
  }

  async function handleSubmitMoveToServiceRequest({ id: newRequestId }) {
    const action =
      REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.MoveToAnotherServiceRequest

    setPerformingAction(true)

    return moveRequestThreadToRequest({
      newRequestId,
      requestId,
      uuid: mailUuid,
    })
      .then(() => {
        handleAddSuccessNotification(
          getRequestProfileTabEmailsItemActionsNotificationTitleKey(action)
        )
        setModal(null)
        onReloadList?.()

        return onThreadRemoved?.()
      })
      .catch(() => {
        handleAddErrorNotification(
          getRequestProfileTabEmailsItemActionsErrorTitleKey(action)
        )
      })
      .finally(() => {
        setPerformingAction(false)
      })
  }

  function handleMoveToServiceRequest() {
    setModal(
      <AttachServiceRequestsModal
        actionsProps={{
          buttonAttachProps: {
            text: 'Modal.TabEmailsMoveToServiceRequestSubmitKey',
          },
        }}
        counterProps={{ text: 'Modal.TabEmailsServiceRequestsCounterKey' }}
        excludeIds={[requestId]}
        headerProps={{ text: 'Modal.TabEmailsMoveToServiceRequestTitleKey' }}
        onAttach={handleSubmitMoveToServiceRequest}
        onClose={handleCloseNestedModal}
      />
    )
  }

  const { handleClick: handleLinkClick } = useLinkLikeClick()

  const { handleErrorDownload, handleStartCheckStatus } = useFilesDownloadAll({
    onCloseModal: handleCloseNestedModal,
    onSetModal: setModal,
  })

  function handleStartDownloadAll() {
    handleCloseNestedModal()

    performDocumentsMassAction({
      data: {
        action: DOCUMENTS_ACTION_DOWNLOAD,
        all: true,
        filters: { parent: directoryId, sub_menu: SUBMENU_ALL_SECTION },
        object_id: String(requestId),
        payload: EMPTY_OBJECT,
        scope: FILE_SCOPE_REQUEST,
      },
      outboundUuid,
    })
      .then(({ data, status }) => {
        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleStartCheckStatus(asyncTask, status)
        }
      })
      .catch(handleErrorDownload)
  }

  function handleClickDownloadAll() {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{ submitText: t('Modal.DownloadAllFilesBtnKey') }}
        description={t('Modal.AttentionFilesDownloadMessageKey')}
        title={t('Common.AttentionModalTitleKey')}
        onCancel={handleCloseNestedModal}
        onSubmit={handleStartDownloadAll}
      />
    )
  }

  async function handleMarkAsUnread() {
    const action =
      REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.MarkAsUnread

    setPerformingAction(true)

    return toggleRequestThreadRead({
      outboundUuid,
      read: false,
      requestId,
      uuid: mailUuid,
    })
      .then(() => {
        handleAddSuccessNotification(
          getRequestProfileTabEmailsItemActionsNotificationTitleKey(action)
        )
        onReloadList?.()
        onClose?.()
      })
      .catch(() => {
        handleAddErrorNotification(
          getRequestProfileTabEmailsItemActionsErrorTitleKey(action)
        )
      })
      .finally(() => {
        setPerformingAction(false)
      })
  }

  function handleClickPrint(event) {
    handleLinkClick({
      event,
      link: getRequestThreadPrintPageLink({
        id: mailUuid,
        outboundUuid,
        requestId,
      }),
      target: HTML_LINK_TARGET.blank,
    })
  }

  const itemForActions = {
    activities: entityData?.activities ?? EMPTY_LIST,
    audience: entityData?.audience,
    notes: entityData?.notes ?? EMPTY_STRING,
    permissions,
    uuid: mailUuid,
  }

  // Mark-unread closes this host, move and delete switch it to the next thread
  // (or close it) after their API call — prompt for its dirty replies first so
  // cancelling stops the operation.
  function guardHostClosingAction(action) {
    if (confirmBeforeAction) {
      confirmBeforeAction(action, { scope: minimizedModalScope })

      return
    }

    action()
  }

  function handleClickAction({ event, value }) {
    switch (value) {
      case REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.ToggleStarred:
        return setImportant(mailUuid, !starred)
      case REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.Print:
        return handleClickPrint(event)
      case REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.DownloadFiles:
        return handleClickDownloadAll()
      case REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.Delete:
        return guardHostClosingAction(handleDeleteThread)
      case REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.MoveToAnotherServiceRequest:
        return guardHostClosingAction(handleMoveToServiceRequest)
      case REQUEST_PROFILE_TAB_EMAILS_THREAD_MODAL_HEADER_ACTION.MarkAsUnread:
        return guardHostClosingAction(handleMarkAsUnread)
      default:
        return setItemAction(value, itemForActions)
    }
  }

  const hasFiles = getHasFiles(messages)

  const iconBarActions = getIconBarActions({
    directoryId,
    hasFiles,
    permissions,
    starred,
    t,
  })

  const menuActions = getMenuActions({
    directoryId,
    hasFiles,
    permissions,
    starred,
    t,
  })

  return {
    handleClickAction,
    iconBarActions,
    isPerformingAction,
    menuActions,
  }
}
